import type { CountryCode } from "@freightsimple/generated-dashboard-openapi-client/models/CountryCode";
import { getCountriesOfHoliday } from "@freightsimple/shared/holidays/holidays";
import { asLocalDate } from "@freightsimple/time/branded-types";
import {
  addDays,
  addMonths,
  isLocalDateAfter,
  today,
} from "@freightsimple/time/helpers";
import type { LocalDate } from "@freightsimple/time/branded-types";
import { pickupHolidaysOn } from "@/Shared/Dates/pickupHolidays";
import {
  PickupDatePicker,
  type PickupDateHoliday,
} from "@freightsimple/ui-core/DateTime/Controls/PickupDatePicker";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import moment from "moment";
import { useMemo } from "react";

interface PickupDateInputProps {
  country?: CountryCode | undefined;
  date: string;
  onDateChange: (date: moment.Moment) => void;
}

/**
 * ui-core stays holiday-library-agnostic, so the app hands it the holidays that
 * fall inside the booking window. Which ones those are depends on how much we
 * know about the pickup — see `pickupHolidaysOn`.
 */
function holidaysBetween(
  from: LocalDate,
  to: LocalDate,
  country: CountryCode | undefined,
): PickupDateHoliday[] {
  const found: PickupDateHoliday[] = [];
  for (let date = from; !isLocalDateAfter(date, to); date = addDays(date, 1)) {
    const onThisDay = pickupHolidaysOn(date, country);
    if (onThisDay.length === 0) {
      continue;
    }

    found.push({
      date,
      name: (
        <Stack gap={1}>
          {onThisDay.map((holiday) => (
            <T key={holiday.description}>
              {`⚠️ ${holiday.description} in ${getCountriesOfHoliday(holiday).join(" and ")}.`}
            </T>
          ))}
        </Stack>
      ),
    });
  }
  return found;
}

export function BookShipmentPickupDateInput(props: PickupDateInputProps) {
  const firstAllowedDate = today();
  const lastAllowedDate = addMonths(firstAllowedDate, 1);

  const holidays = useMemo(
    () => holidaysBetween(firstAllowedDate, lastAllowedDate, props.country),
    [firstAllowedDate, lastAllowedDate, props.country],
  );

  return (
    <PickupDatePicker
      value={asLocalDate(props.date)}
      onChange={(date) => props.onDateChange(moment(date))}
      referenceDate={firstAllowedDate}
      latestDate={lastAllowedDate}
      holidays={holidays}
    />
  );
}
